/**
 * e-Invoice Create/Edit Form.
 * Handles: supplier (auto from settings), buyer, line items with SST calculation,
 * dates, currency, save as draft, submit direct to LHDN.
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Trash2, Save, Send, RefreshCw, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyParty = {
  name: '', tin: '', registration_number: '', sst_registration: '',
  email: '', phone: '', address_line_1: '', city: '', state: '', postcode: '', country: 'MYS',
};

const emptyLine = () => ({
  description: '', classification_code: '028',
  quantity: 1, unit_price: 0, unit_of_measure: 'C62',
  discount: 0, tax_type: '06', tax_rate: 0,
});

const EInvoiceFormPage = () => {
  const { invoiceId } = useParams();
  const isEdit = invoiceId && invoiceId !== 'new';
  const navigate = useNavigate();

  const [form, setForm] = useState({
    document_type: 'Invoice',
    issue_date: new Date().toISOString().slice(0, 10),
    due_date: '',
    currency: 'MYR',
    supplier: { ...emptyParty, name: 'Underads Sdn Bhd' },
    buyer: { ...emptyParty },
    line_items: [emptyLine()],
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [ref, setRef] = useState({ classification_codes: [], tax_types: [] });
  const [readOnly, setReadOnly] = useState(false);
  const [status, setStatus] = useState('Draft');
  const [myinvoisData, setMyinvoisData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [refRes, settings] = await Promise.all([
          axios.get(`${API}/einvoice/reference`, { withCredentials: true }),
          axios.get(`${API}/einvoice/supplier-settings`, { withCredentials: true }),
        ]);
        setRef(refRes.data);
        if (!isEdit && settings.data) {
          setForm((f) => ({ ...f, supplier: { ...emptyParty, ...settings.data } }));
        }
      } catch (err) {
        console.error(err);
      }
    })();
  }, [isEdit]);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API}/einvoice/${invoiceId}`, { withCredentials: true });
        const inv = res.data;
        setForm({
          document_type: inv.document_type,
          issue_date: inv.issue_date,
          due_date: inv.due_date || '',
          currency: inv.currency,
          supplier: { ...emptyParty, ...inv.supplier },
          buyer: { ...emptyParty, ...inv.buyer },
          line_items: inv.line_items.map((li) => ({ ...emptyLine(), ...li })),
          notes: inv.notes || '',
        });
        setStatus(inv.status);
        setMyinvoisData({
          uuid: inv.myinvois_uuid, long_id: inv.myinvois_long_id,
          qr: inv.myinvois_qr_code, rejection: inv.myinvois_rejection_reason,
        });
        setReadOnly(!['Draft', 'Rejected'].includes(inv.status));
      } catch (err) {
        setError(err?.response?.data?.detail || 'Ralat memuat invoice');
      } finally {
        setLoading(false);
      }
    })();
  }, [invoiceId, isEdit]);

  // Derived totals
  const totals = form.line_items.reduce((acc, li) => {
    const sub = Math.max(0, (Number(li.quantity) || 0) * (Number(li.unit_price) || 0) - (Number(li.discount) || 0));
    const tax = sub * ((Number(li.tax_rate) || 0) / 100);
    acc.subtotal += sub;
    acc.tax += tax;
    acc.discount += Number(li.discount) || 0;
    return acc;
  }, { subtotal: 0, tax: 0, discount: 0 });
  const grand = totals.subtotal + totals.tax;

  const updateParty = (key, field, value) => {
    setForm((f) => ({ ...f, [key]: { ...f[key], [field]: value } }));
  };

  const updateLine = (idx, field, value) => {
    setForm((f) => ({
      ...f,
      line_items: f.line_items.map((li, i) => i === idx ? { ...li, [field]: value } : li),
    }));
  };

  const addLine = () => setForm((f) => ({ ...f, line_items: [...f.line_items, emptyLine()] }));
  const removeLine = (idx) => setForm((f) => ({ ...f, line_items: f.line_items.filter((_, i) => i !== idx) }));

  const save = async ({ thenSubmit = false } = {}) => {
    setError(null);
    setLoading(true);
    try {
      const payload = {
        ...form,
        line_items: form.line_items.map((li) => ({
          ...li,
          quantity: Number(li.quantity) || 0,
          unit_price: Number(li.unit_price) || 0,
          discount: Number(li.discount) || 0,
          tax_rate: Number(li.tax_rate) || 0,
        })),
      };
      const res = isEdit
        ? await axios.put(`${API}/einvoice/${invoiceId}`, payload, { withCredentials: true })
        : await axios.post(`${API}/einvoice`, payload, { withCredentials: true });
      const saved = res.data;
      if (thenSubmit) {
        const submitRes = await axios.post(`${API}/einvoice/${saved.id}/submit`, {}, { withCredentials: true });
        alert(`Submitted ke LHDN: ${submitRes.data.status}`);
      }
      navigate('/einvoice');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : (detail?.[0]?.msg || err.message));
    } finally {
      setLoading(false);
    }
  };

  if (loading && !form.line_items.length) {
    return <div className="flex justify-center py-20"><RefreshCw className="w-5 h-5 animate-spin text-gray-400" /></div>;
  }

  return (
    <div className="space-y-5" data-testid="einvoice-form">
      <button onClick={() => navigate('/einvoice')} className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900">
        <ArrowLeft className="w-4 h-4" /> Kembali ke senarai
      </button>

      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEdit ? `Edit ${form.document_type}` : `${form.document_type} Baru`}
          </h1>
          {isEdit && <p className="text-xs text-gray-500 mt-1">Status: <span className="font-semibold">{status}</span></p>}
        </div>
        {readOnly && (
          <div className="bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold px-3 py-1.5 rounded-full flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" /> Mod Baca Sahaja — {status}
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm" data-testid="einvoice-form-error">
          {error}
        </div>
      )}

      {myinvoisData?.rejection && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm" data-testid="myinvois-rejection">
          <b>Sebab penolakan MyInvois:</b> {myinvoisData.rejection}
        </div>
      )}

      {/* Top meta */}
      <div className="bg-white rounded-xl border border-gray-100 p-5 grid grid-cols-1 sm:grid-cols-4 gap-4">
        <label className="block">
          <span className="text-xs font-medium text-gray-500">Jenis Dokumen</span>
          <select disabled={readOnly} value={form.document_type} onChange={(e) => setForm({ ...form, document_type: e.target.value })}
            className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm disabled:bg-gray-50">
            {['Invoice', 'Credit Note', 'Debit Note', 'Refund Note'].map(t => <option key={t}>{t}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-medium text-gray-500">Tarikh Keluar</span>
          <input disabled={readOnly} type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })}
            className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm disabled:bg-gray-50" />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-gray-500">Tarikh Jatuh Tempo</span>
          <input disabled={readOnly} type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })}
            className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm disabled:bg-gray-50" />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-gray-500">Mata Wang</span>
          <select disabled={readOnly} value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}
            className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm disabled:bg-gray-50">
            {['MYR', 'USD', 'SGD', 'EUR', 'GBP'].map(c => <option key={c}>{c}</option>)}
          </select>
        </label>
      </div>

      {/* Parties */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {[
          { key: 'supplier', label: 'Supplier (Pembekal — Syarikat Anda)' },
          { key: 'buyer', label: 'Buyer (Pembeli)' },
        ].map(({ key, label }) => (
          <div key={key} className="bg-white rounded-xl border border-gray-100 p-5 space-y-3" data-testid={`party-${key}`}>
            <h3 className="font-semibold text-gray-800">{label}</h3>
            <div className="grid grid-cols-2 gap-3">
              {[
                ['name', 'Nama'],
                ['tin', 'TIN'],
                ['registration_number', 'No. SSM / IC'],
                ['sst_registration', 'No. SST'],
                ['email', 'Emel'],
                ['phone', 'Telefon'],
              ].map(([field, lbl]) => (
                <label key={field} className={`block ${field === 'name' ? 'col-span-2' : ''}`}>
                  <span className="text-xs font-medium text-gray-500">{lbl}</span>
                  <input disabled={readOnly} type="text" value={form[key][field] || ''}
                    onChange={(e) => updateParty(key, field, e.target.value)}
                    data-testid={`${key}-${field}`}
                    className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm disabled:bg-gray-50" />
                </label>
              ))}
              <label className="block col-span-2">
                <span className="text-xs font-medium text-gray-500">Alamat</span>
                <input disabled={readOnly} type="text" value={form[key].address_line_1 || ''}
                  onChange={(e) => updateParty(key, 'address_line_1', e.target.value)}
                  className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm disabled:bg-gray-50" />
              </label>
              <label><span className="text-xs text-gray-500">Bandar</span>
                <input disabled={readOnly} type="text" value={form[key].city || ''} onChange={(e) => updateParty(key, 'city', e.target.value)} className="mt-1 w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-50" />
              </label>
              <label><span className="text-xs text-gray-500">Poskod</span>
                <input disabled={readOnly} type="text" value={form[key].postcode || ''} onChange={(e) => updateParty(key, 'postcode', e.target.value)} className="mt-1 w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-50" />
              </label>
            </div>
          </div>
        ))}
      </div>

      {/* Line items */}
      <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid="line-items-section">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-800">Item Invoice</h3>
          {!readOnly && (
            <button onClick={addLine} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700" data-testid="add-line-btn">
              <Plus className="w-4 h-4" /> Tambah Item
            </button>
          )}
        </div>
        <div className="space-y-3">
          {form.line_items.map((li, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 items-end p-3 bg-gray-50 rounded-lg" data-testid={`line-${idx}`}>
              <label className="col-span-12 sm:col-span-4">
                <span className="text-xs text-gray-500">Penerangan</span>
                <input disabled={readOnly} type="text" value={li.description} onChange={(e) => updateLine(idx, 'description', e.target.value)}
                  className="mt-1 w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm disabled:bg-white" />
              </label>
              <label className="col-span-6 sm:col-span-2">
                <span className="text-xs text-gray-500">Klasifikasi</span>
                <select disabled={readOnly} value={li.classification_code} onChange={(e) => updateLine(idx, 'classification_code', e.target.value)}
                  className="mt-1 w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs disabled:bg-white">
                  {ref.classification_codes.slice(0, 35).map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
                </select>
              </label>
              <label className="col-span-3 sm:col-span-1">
                <span className="text-xs text-gray-500">Qty</span>
                <input disabled={readOnly} type="number" step="0.01" value={li.quantity} onChange={(e) => updateLine(idx, 'quantity', e.target.value)}
                  className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm disabled:bg-white" />
              </label>
              <label className="col-span-6 sm:col-span-2">
                <span className="text-xs text-gray-500">Harga</span>
                <input disabled={readOnly} type="number" step="0.01" value={li.unit_price} onChange={(e) => updateLine(idx, 'unit_price', e.target.value)}
                  className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm disabled:bg-white" />
              </label>
              <label className="col-span-3 sm:col-span-1">
                <span className="text-xs text-gray-500">Diskaun</span>
                <input disabled={readOnly} type="number" step="0.01" value={li.discount} onChange={(e) => updateLine(idx, 'discount', e.target.value)}
                  className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm disabled:bg-white" />
              </label>
              <label className="col-span-3 sm:col-span-1">
                <span className="text-xs text-gray-500">Cukai %</span>
                <input disabled={readOnly} type="number" step="0.1" value={li.tax_rate} onChange={(e) => updateLine(idx, 'tax_rate', e.target.value)}
                  className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm disabled:bg-white" />
              </label>
              <div className="col-span-6 sm:col-span-1 text-right">
                <p className="text-xs text-gray-500">Subtotal</p>
                <p className="text-sm font-semibold truncate">RM {Math.max(0, (Number(li.quantity) || 0) * (Number(li.unit_price) || 0) - (Number(li.discount) || 0)).toFixed(2)}</p>
              </div>
              {!readOnly && (
                <button onClick={() => removeLine(idx)} disabled={form.line_items.length === 1}
                  data-testid={`remove-line-${idx}`}
                  className="col-span-3 sm:col-span-12 sm:w-auto sm:ml-auto text-red-500 hover:text-red-700 disabled:text-gray-300 flex items-center justify-center p-2 rounded-lg hover:bg-red-50">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Totals */}
        <div className="mt-5 flex justify-end">
          <div className="w-full sm:w-80 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span>RM {totals.subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Diskaun</span><span className="text-red-600">-RM {totals.discount.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Cukai</span><span>RM {totals.tax.toFixed(2)}</span></div>
            <div className="flex justify-between border-t border-gray-200 pt-2 text-base font-bold">
              <span>Jumlah</span><span className="text-red-700" data-testid="grand-total">RM {grand.toFixed(2)} {form.currency}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Notes */}
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <label className="block">
          <span className="text-xs font-medium text-gray-500">Nota</span>
          <textarea disabled={readOnly} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2}
            className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm disabled:bg-gray-50" />
        </label>
      </div>

      {/* Actions */}
      {!readOnly && (
        <div className="flex flex-wrap justify-end gap-2">
          <button onClick={() => save()} disabled={loading} data-testid="save-draft-btn"
            className="px-5 py-2 bg-gray-100 border border-gray-200 rounded-lg font-medium text-gray-700 hover:bg-gray-200 flex items-center gap-2 disabled:opacity-50">
            <Save className="w-4 h-4" /> Simpan Draft
          </button>
          <button onClick={() => save({ thenSubmit: true })} disabled={loading} data-testid="save-submit-btn"
            className="px-5 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 flex items-center gap-2 disabled:opacity-50">
            <Send className="w-4 h-4" /> Simpan & Submit ke LHDN
          </button>
        </div>
      )}
    </div>
  );
};

export default EInvoiceFormPage;
